import React, { useState, useEffect } from 'react';
import { Button, Box } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import XmlMerger from '../apps/XmlMerger';
import KeywordCountDownload from '../apps/KeywordCountDownload';
import ErrorDialog from './ErrorDialog';
import KeywordForm from './KeywordForm';
import KeywordList from './KeywordList';
import LoadingDialog from './LoadingDialog';

import { initialKeywords, initialExcludedKeywords } from '../apps/KeywordsContext';
import useKeywords from '../hooks/useKeywords';
import { useErrorHandling } from '../hooks/useErrorHandling';
import { useMerge } from '../hooks/useMerge';
import { Messages } from '../constants/Messages';

export interface Keyword {
    id: number;
    keyword: string;
    count: number;
    clause: string;
    isEditing?: boolean;
    isExcluded?: boolean;
}

interface KeywordTableProps {
    files: FileList | null;
    onKeywordsChange: (updatedKeywords: Keyword[]) => void;
    onExcludedKeywordsChange: (updatedExcludedKeywords: Keyword[]) => void;
    mergeStatus: string;
    setMergeStatus: (status: string) => void;
    onMerge: (xmlDoc: Document | null) => void;
}

const KeywordTable: React.FC<KeywordTableProps> = ({
    files,
    onKeywordsChange,
    onExcludedKeywordsChange,
    mergeStatus,
    setMergeStatus,
    onMerge
}) => {
    const { keywords, setKeywords, excludedKeywords, setExcludedKeywords } = useKeywords(
        'keywords',
        'excludedKeywords',
        initialKeywords,
        initialExcludedKeywords
    );

    const [searchInfoMessage, setSearchInfoMessage] = useState('');
    const [excludeInfoMessage, setExcludeInfoMessage] = useState('');
    const [totalKeywordCount, setTotalKeywordCount] = useState(0);
    const [totalExcludedKeywordCount, setTotalExcludedKeywordCount] = useState(0);

    const {
        errorDialogOpen,
        errorMessage,
        errorKeywordId,
        isErrorKeywordExcluded,
        isErrorState,
        previousKeyword,
        handleCloseErrorDialog,
        handleEmptyKeywordError,
        handleDuplicateKeywordError,
        handleInvalidCharacterError,
        setPreviousKeyword,
        setErrorKeywordId,
        setIsErrorKeywordExcluded,
        setIsErrorState,
        setErrorHandled
    } = useErrorHandling();

    const {
        shouldMerge,
        setShouldMerge,
        mergedText,
        mergeSuccessMessage,
        handleMergeClick,
        setMergedText,
        setMergeSuccessMessage
    } = useMerge(files, setMergeStatus);

    const invalidCharacters = /[.*+?^${}()|[\]\\]/;

    const isInvalidKeyword = (keyword: string) => {
        if (keyword.includes('*')) {
            return true;
        }
        if (invalidCharacters.test(keyword) && keyword.length === 1) {
            return true;
        }
        return false;
    };

    const clearMessages = () => {
        setSearchInfoMessage('');
        setExcludeInfoMessage('');
        setMergeSuccessMessage('');
    };

    const addKeyword = (keyword: string, isExcluded: boolean) => {
        clearMessages();
        if (keyword.trim() === '') {
            handleEmptyKeywordError(null, isExcluded);
            return;
        }

        if (isInvalidKeyword(keyword)) {
            handleInvalidCharacterError(null, isExcluded);
            return;
        }

        const keywordExistsInKeywords = keywords.some((k: Keyword) => k.keyword === keyword.trim());
        const keywordExistsInExcludedKeywords = excludedKeywords.some((k: Keyword) => k.keyword === keyword.trim());

        if (keywordExistsInKeywords || keywordExistsInExcludedKeywords) {
            const location = keywordExistsInKeywords ? '検索キーワード' : '除外キーワード';
            handleDuplicateKeywordError(null, isExcluded, location);
            return;
        }

        const newId = (isExcluded ? excludedKeywords : keywords).length > 0
            ? (isExcluded ? excludedKeywords : keywords)[(isExcluded ? excludedKeywords : keywords).length - 1].id + 1
            : 1;
        const updatedKeywords = [...(isExcluded ? excludedKeywords : keywords), { id: newId, keyword: keyword.trim(), count: 0, clause: '', isExcluded }];
        if (isExcluded) {
            setExcludedKeywords(updatedKeywords);
            onExcludedKeywordsChange(updatedKeywords);
        } else {
            setKeywords(updatedKeywords);
            onKeywordsChange(updatedKeywords);
        }
        setMergeStatus('idle');
        isExcluded ? setExcludeInfoMessage(Messages.EXCLUDE_KEYWORD_ADDED) : setSearchInfoMessage(Messages.SEARCH_KEYWORD_ADDED);
    };

    const addSearchKeyword = (keyword: string) => addKeyword(keyword, false);
    const addExcludeKeyword = (keyword: string) => addKeyword(keyword, true);

    const deleteKeywordById = (id: number, isExcluded: boolean) => {
        clearMessages();
        const targetKeywords = isExcluded ? excludedKeywords : keywords;
        const updatedKeywords = targetKeywords.filter((keyword: Keyword) => keyword.id !== id);

        if (isExcluded) {
            setExcludedKeywords(updatedKeywords);
            onExcludedKeywordsChange(updatedKeywords);
            // localStorage.setItem('excludedKeywords', JSON.stringify(updatedKeywords));
        } else {
            setKeywords(updatedKeywords);
            onKeywordsChange(updatedKeywords);
            // localStorage.setItem('keywords', JSON.stringify(updatedKeywords));
        }

        setMergeStatus('idle');
        if (isErrorState && errorKeywordId === id && isErrorKeywordExcluded === isExcluded) {
            setErrorKeywordId(null);
            setIsErrorKeywordExcluded(null);
            setIsErrorState(false);
            setErrorHandled(false);
        }
    };

    const updateKeyword = (id: number, newKeywordValue: string, isExcluded: boolean) => {
        clearMessages();
        const targetKeywords = isExcluded ? excludedKeywords : keywords;
        const updatedKeywords = targetKeywords.map((keyword: Keyword) =>
            keyword.id === id ? { ...keyword, keyword: newKeywordValue } : keyword
        );

        if (isExcluded) {
            setExcludedKeywords(updatedKeywords);
            onExcludedKeywordsChange(updatedKeywords);
        } else {
            setKeywords(updatedKeywords);
            onKeywordsChange(updatedKeywords);
        }
        setMergeStatus('idle');
    };

    const startEditingKeywordById = (id: number, isExcluded: boolean) => {
        clearMessages();
        const targetKeywords = isExcluded ? excludedKeywords : keywords;
        const updatedKeywords = targetKeywords.map((keyword: Keyword) =>
            keyword.id === id ? { ...keyword, isEditing: true } : keyword
        );
        const currentKeyword = targetKeywords.find((keyword: Keyword) => keyword.id === id);
        if (currentKeyword) {
            setPreviousKeyword(currentKeyword.keyword);
        }
        if (isExcluded) {
            setExcludedKeywords(updatedKeywords);
        } else {
            setKeywords(updatedKeywords);
        }
    };

    const finishEditingKeyword = (id: number, newKeyword: string, isExcluded: boolean) => {
        clearMessages();
        const trimmedKeyword = newKeyword ? newKeyword.trim() : '';
        if (!trimmedKeyword) {
            handleEmptyKeywordError(id, isExcluded);
            revertKeywordById(id, isExcluded);
            return;
        }

        if (isInvalidKeyword(trimmedKeyword)) {
            handleInvalidCharacterError(id, isExcluded);
            revertKeywordById(id, isExcluded);
            return;
        }

        const targetKeywords = isExcluded ? excludedKeywords : keywords;
        const otherKeywords = isExcluded ? keywords : excludedKeywords;
        if (targetKeywords.some((k: Keyword) => k.keyword === trimmedKeyword && k.id !== id) ||
            otherKeywords.some((k: Keyword) => k.keyword === trimmedKeyword)) {
            handleDuplicateKeywordError(id, isExcluded, isExcluded ? '除外キーワード' : '検索キーワード');
            revertKeywordById(id, isExcluded);
        } else {
            const updatedKeywords = targetKeywords.map((keyword: Keyword) =>
                keyword.id === id ? { ...keyword, keyword: trimmedKeyword, isEditing: false } : keyword
            );
            if (isExcluded) {
                setExcludedKeywords(updatedKeywords);
                onExcludedKeywordsChange(updatedKeywords);
            } else {
                setKeywords(updatedKeywords);
                onKeywordsChange(updatedKeywords);
            }
            isExcluded ? setExcludeInfoMessage('') : setSearchInfoMessage('');
            setErrorKeywordId(null);
            setIsErrorKeywordExcluded(null);
            setIsErrorState(false);
            setErrorHandled(false);
            setMergeStatus('idle');
        }
    };

    const revertKeywordById = (id: number, isExcluded: boolean) => {
        clearMessages();
        const targetKeywords = isExcluded ? excludedKeywords : keywords;
        const updatedKeywords = targetKeywords.map((keyword: Keyword) =>
            keyword.id === id ? { ...keyword, keyword: previousKeyword, isEditing: false } : keyword
        );
        if (isExcluded) {
            setExcludedKeywords(updatedKeywords);
            onExcludedKeywordsChange(updatedKeywords);
        } else {
            setKeywords(updatedKeywords);
            onKeywordsChange(updatedKeywords);
        }
    };

    const handleKeywordCountUpdate = (keyword: string, count: number) => {
        setKeywords((prevKeywords: Keyword[]) =>
            prevKeywords.map((k: Keyword) =>
                k.keyword === keyword ? { ...k, count } : k
            )
        );
    };

    const handleExcludedKeywordCountUpdate = (keyword: string, count: number) => {
        setExcludedKeywords((prevKeywords: Keyword[]) =>
            prevKeywords.map((k: Keyword) =>
                k.keyword === keyword ? { ...k, count } : k
            )
        );
    };

    const handleMergeClickWithSpinner = () => {
        clearMessages();
        setMergeStatus('processing');
        handleMergeClick();
    };

    const handleKeywordReset = () => {
        const confirmed = window.confirm("編集したキーワードが削除され、初期状態に戻ります。よろしいですか？");
        if (confirmed) {
            localStorage.setItem('keywords', JSON.stringify(initialKeywords));
            localStorage.setItem('excludedKeywords', JSON.stringify(initialExcludedKeywords));
            window.location.reload();
        }
    };

    useEffect(() => {
        if (mergeStatus === 'idle' || mergeStatus !== 'completed') {
            setTotalKeywordCount(0);
            setTotalExcludedKeywordCount(0);
        } else {
            setTotalKeywordCount(keywords.reduce((total, keyword) => total + keyword.count, 0));
            setTotalExcludedKeywordCount(excludedKeywords.reduce((total, keyword) => total + keyword.count, 0));
        }
    }, [mergeStatus, keywords, excludedKeywords]);

    return (
        <>
            <ErrorDialog open={errorDialogOpen} errorMessage={errorMessage} onClose={handleCloseErrorDialog} />
            {files && shouldMerge && (
                <XmlMerger files={files} onMerge={(xmlDoc) => {
                    setShouldMerge(false);
                    if (xmlDoc) {
                        // xmlDocから直接プレーンテキストを抽出（XMLSerializer→DOMParserの重複を回避）
                        const paragraphs = Array.from(xmlDoc.getElementsByTagName('w:p')).map(paragraph => {
                            return Array.from(paragraph.getElementsByTagName('w:t'))
                                .map(node => node.textContent ?? "")
                                .join('');
                        });
                        setMergedText(paragraphs.join('\n'));
                        setMergeStatus('completed');
                        setMergeSuccessMessage(Messages.MERGE_SUCCESS);
                        onMerge(xmlDoc);
                    } else {
                        setMergedText(null);
                        setMergeStatus('error');
                    }
                }} />
            )}
            <div className='step-accordion'>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <h3>STEP2: 検索・除外キーワードを設定してください。</h3>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className="table-container">
                            <div className='keyword-section'>
                                <KeywordForm
                                    onAddKeyword={addSearchKeyword}
                                    placeholder="キーワードを入力"
                                    buttonLabel="追加"
                                    infoMessage={searchInfoMessage}
                                    setInfoMessage={setSearchInfoMessage}
                                    title="検索キーワード"
                                />
                                <KeywordList
                                    keywords={keywords}
                                    excludedKeywords={excludedKeywords}
                                    isExcluded={false}
                                    parsedPlainText={mergedText}
                                    mergeStatus={mergeStatus}
                                    onUpdateKeyword={updateKeyword}
                                    onDeleteKeyword={deleteKeywordById}
                                    onStartEditingKeyword={startEditingKeywordById}
                                    onFinishEditingKeyword={finishEditingKeyword}
                                    handleKeywordCountUpdate={handleKeywordCountUpdate}
                                />
                                <div className="total-hits">
                                    <p>検索キーワード合計ヒット数: {totalKeywordCount}</p>
                                </div>
                            </div>
                            <div className='keyword-section'>
                                <KeywordForm
                                    onAddKeyword={addExcludeKeyword}
                                    placeholder="キーワードを入力"
                                    buttonLabel="追加"
                                    infoMessage={excludeInfoMessage}
                                    setInfoMessage={setExcludeInfoMessage}
                                    title="除外キーワード"
                                />
                                <KeywordList
                                    keywords={excludedKeywords}
                                    excludedKeywords={excludedKeywords}
                                    isExcluded={true}
                                    parsedPlainText={mergedText}
                                    mergeStatus={mergeStatus}
                                    onUpdateKeyword={updateKeyword}
                                    onDeleteKeyword={deleteKeywordById}
                                    onStartEditingKeyword={startEditingKeywordById}
                                    onFinishEditingKeyword={finishEditingKeyword}
                                    handleKeywordCountUpdate={handleExcludedKeywordCountUpdate}
                                />
                                <div className="total-hits">
                                    <p>除外キーワード合計ヒット数: {totalExcludedKeywordCount}</p>
                                </div>
                            </div>
                        </div>
                        <div className="success-message-container">
                            <p className='success-message'>{mergeSuccessMessage}</p>
                        </div>

                        <div className='button-wrapper'>
                            <Box className="button-container">
                                <Button
                                    className='download-button'
                                    onClick={handleMergeClickWithSpinner}
                                    variant='contained'
                                    disabled={!files || mergeStatus === 'processing'}
                                >
                                    Wordファイルを解析する
                                </Button>
                                <Box className="spacer" />
                                <KeywordCountDownload
                                    keywords={keywords}
                                    excludedKeywords={excludedKeywords}
                                    mergeStatus={mergeStatus}
                                />
                                <Box className="spacer" />
                                <Button
                                    className='download-button'
                                    onClick={handleKeywordReset}
                                    variant='contained'
                                >
                                    キーワードを初期化する
                                </Button>
                            </Box>
                            <LoadingDialog open={mergeStatus === 'processing'} message="" />
                        </div>

                    </AccordionDetails>
                </Accordion>
            </div>
        </>
    );
};

export default KeywordTable;
